import React, {useCallback, useEffect, useRef} from "react";
import ReactPlayer from "react-player";
import {IS_IOS_DEVICE} from "../videoPlayer.utils";

interface UseIosFullscreenParams {
    playerRef: React.RefObject<ReactPlayer>;
    playerUrl: string | undefined;
    isLiveType: boolean;
    destroyHls: () => void;
    handleHls: (url: string, options: {isLiveContent: boolean}) => void;
    subtitlesEnabledRef: React.RefObject<boolean>;
    selectedSubtitleRef: React.RefObject<string>;
    // Applied to native video.audioTracks at loadedmetadata so QuickTime starts
    // with the same audio language the user had selected in react-player.
    selectedAudioLangRef: React.RefObject<string>;
    // Manifest's declared audio order — audioOrderRef.current[0] is the DEFAULT.
    // When the user's selection differs, we keep hls.js attached during native
    // fullscreen (MSE path) so audio plays reliably in their language instead of
    // reverting to the manifest DEFAULT via the broken native HLS audioTrack API.
    audioOrderRef: React.RefObject<string[]>;
    // Syncs react-player's subtitlesEnabled to the final native textTrack mode at
    // fullscreen exit — captures whatever the user did in QuickTime's CC menu.
    syncSubtitlesEnabled: (enabled: boolean) => void;
    // Set before handleHls reinitializes so parseM3u8 restores the audio language
    // the user selected in QuickTime, not the pre-enter react-player state.
    syncAudioLang: (lang: string) => void;
    // Written here; read by useVideoPlayer to block the API-position resume seek.
    iosExitSeekRef: React.MutableRefObject<number | null>;
}

const dbgFs = (...args: any[]) => console.log("[SubDebug][Fullscreen]", ...args);

const logVideoTextTracks = (video: HTMLVideoElement, label: string) => {
    const tracks = Array.from({ length: video.textTracks.length }, (_, i) => ({
        label: video.textTracks[i].label,
        language: video.textTracks[i].language,
        kind: video.textTracks[i].kind,
        mode: video.textTracks[i].mode,
    }));
    dbgFs(label, "textTracks:", tracks);
};

// On iOS, hls.js manages audio via MSE so video.audioTracks is empty
// and QuickTime (fullscreen) cannot see language tracks.
// On enter: detach ReactPlayer's hls.js cleanly before setting native
// video.src, preventing its error-recovery from interfering.
// On exit: reattach hls.js — attachMedia overwrites video.src internally.
export const useIosFullscreen = ({
    playerRef,
    playerUrl,
    isLiveType,
    destroyHls,
    handleHls,
    subtitlesEnabledRef,
    selectedSubtitleRef,
    selectedAudioLangRef,
    audioOrderRef,
    syncSubtitlesEnabled,
    syncAudioLang,
    iosExitSeekRef,
}: UseIosFullscreenParams) => {
    const cleanupRef = useRef<(() => void) | null>(null);
    const savedReactHlsRef = useRef<any>(null);
    const addTrackCleanupRef = useRef<(() => void) | null>(null);
    // Tracks whether the current fullscreen session took the MSE path (no detach)
    // so the exit handler can skip the matching reattach/handleHls.
    const usedMsePathRef = useRef<boolean>(false);

    useEffect(() => () => { cleanupRef.current?.(); }, []);

    const attach = useCallback(
        (video: HTMLVideoElement) => {
            if (!IS_IOS_DEVICE || !playerUrl?.includes(".m3u8")) return;

            cleanupRef.current?.();
            dbgFs("attach: registering webkitbeginfullscreen / webkitendfullscreen listeners");

            let savedTime = 0;
            // Last currentTime observed via timeupdate during native fullscreen.
            // video.currentTime at webkitendfullscreen is stale on iOS — the native
            // player hasn't synced its position back to the element yet at that moment.
            let trackedTime = 0;
            const onTimeUpdate = () => { trackedTime = video.currentTime; };

            // Timestamp of the last applyNativeSubtitles call. The textTrackList
            // "change" listener uses this to distinguish our own mode mutations
            // (which we should ignore) from a user toggle in QuickTime's CC menu
            // (which we sync back to react-player). 100ms is enough for WebKit's
            // change event to fire after the mode setter, comfortably below any
            // realistic user reaction time.
            let lastApplyAt = 0;
            const APPLY_QUIET_WINDOW_MS = 100;

            // Applies the selected subtitle to native iOS text tracks.
            // Called at loadedmetadata and again via addtrack for tracks added later.
            const applyNativeSubtitles = (callSite = "unknown") => {
                // Marks the start of our own mode mutations so the textTracks
                // "change" listener can ignore the resulting echoes.
                lastApplyAt = Date.now();
                const enabled = subtitlesEnabledRef.current;
                const target = selectedSubtitleRef.current;
                const count = video.textTracks.length;

                // Snapshot BEFORE changing modes
                const before = Array.from({ length: count }, (_, i) =>
                    `[${i}] label="${video.textTracks[i].label}" kind="${video.textTracks[i].kind}" lang="${video.textTracks[i].language}" mode=${video.textTracks[i].mode}`
                ).join(" | ");

                dbgFs(`applyNativeSubtitles [${callSite}]: enabled=${enabled} target="${target}" trackCount=${count} BEFORE: ${before || "none"}`);

                if (!enabled) {
                    // Force every subtitle track to disabled so QuickTime's AUTOSELECT
                    // (from the manifest) doesn't render subs against the user preference.
                    // react-player state is the source of truth; on re-entry the native CC
                    // menu remembers the prior OFF choice and matches this state.
                    let changed = 0;
                    for (let i = 0; i < count; i++) {
                        const t = video.textTracks[i];
                        if (t.mode !== "disabled") {
                            t.mode = "disabled";
                            changed++;
                        }
                    }
                    const after = Array.from({ length: count }, (_, i) =>
                        `[${i}] kind="${video.textTracks[i].kind}" mode=${video.textTracks[i].mode}`
                    ).join(" | ");
                    dbgFs(`applyNativeSubtitles [${callSite}]: enabled=false — forced ${changed} subtitle track(s) to disabled AFTER: ${after}`);
                    return;
                }
                if (!target) {
                    for (let i = 0; i < count; i++) {
                        video.textTracks[i].mode = "disabled";
                    }
                    dbgFs(`applyNativeSubtitles [${callSite}]: disabled all (no target)`);
                    return;
                }

                // QuickTime uses the LAST matching track as its native subtitle track
                // (it adds its own rendition after loadedmetadata via addtrack and may
                // auto-disable the earlier one). Setting the first match would undo
                // QuickTime's own selection and leave the native menu showing "Off".
                let lastMatchIdx = -1;
                for (let i = 0; i < count; i++) {
                    const track = video.textTracks[i];
                    const isMatch =
                        track.label === target ||
                        track.label?.toLowerCase() === target.toLowerCase() ||
                        track.language === target;
                    if (isMatch) lastMatchIdx = i;
                }
                const applied = lastMatchIdx !== -1;
                for (let i = 0; i < count; i++) {
                    video.textTracks[i].mode = i === lastMatchIdx ? "showing" : "disabled";
                }

                // Snapshot AFTER to verify WebKit honored the change
                const after = Array.from({ length: count }, (_, i) =>
                    `[${i}] label="${video.textTracks[i].label}" kind="${video.textTracks[i].kind}" mode=${video.textTracks[i].mode}`
                ).join(" | ");
                dbgFs(`applyNativeSubtitles [${callSite}]: applied=${applied} AFTER: ${after || "none"}`);
            };

            // Applies the audio track selected in react-player to native iOS audioTracks.
            // In native-HLS mode (video.src set on enter), video.audioTracks may be
            // populated at loadedmetadata or later via addtrack events on audioTracks.
            const applyNativeAudio = (callSite = "unknown") => {
                const audioTracks = (video as any).audioTracks;
                const target = selectedAudioLangRef.current;
                const count = audioTracks?.length ?? 0;
                const beforeDetails = Array.from({length: count}, (_, i) =>
                    `[${i}] label="${audioTracks[i].label}" lang="${audioTracks[i].language}" enabled=${audioTracks[i].enabled}`
                ).join(" | ");
                dbgFs(`applyNativeAudio [${callSite}]: audioCount=${count} target="${target}" BEFORE: ${beforeDetails || "none"}`);
                if (count <= 1 || !target) return;
                let applied = false;
                for (let i = 0; i < count; i++) {
                    const matches =
                        audioTracks[i].label === target ||
                        audioTracks[i].language === target;
                    audioTracks[i].enabled = matches;
                    if (matches) applied = true;
                }
                const afterDetails = Array.from({length: count}, (_, i) =>
                    `[${i}] label="${audioTracks[i].label}" enabled=${audioTracks[i].enabled}`
                ).join(" | ");
                dbgFs(`applyNativeAudio [${callSite}]: applied=${applied} AFTER: ${afterDetails}`);
            };

            const onEnterFullscreen = () => {
                savedTime = video.currentTime;
                trackedTime = savedTime;
                video.addEventListener("timeupdate", onTimeUpdate);
                dbgFs("onEnterFullscreen", { savedTime, isLiveType });
                logVideoTextTracks(video, "onEnterFullscreen");

                const reactHls = (playerRef.current as any)?.getInternalPlayer?.("hls");
                dbgFs("onEnterFullscreen: reactHls available:", !!reactHls, {
                    subtitleTracks: reactHls?.subtitleTracks?.map?.((t: any) => ({ name: t.name })) ?? "none",
                    currentSubtitleTrack: reactHls?.subtitleTrack ?? "n/a",
                });

                // Path decision: when the user picked a non-default audio language,
                // keep hls.js attached (MSE path). iOS plays the QuickTime fullscreen
                // straight off the existing SourceBuffer, so the audio is whatever
                // hls.js has selected — no fragile native-HLS audioTrack switching.
                // When the user is on the manifest DEFAULT (or has no preference yet)
                // there's nothing to override, so we take the original native-HLS path
                // (detach + video.src reassignment) where QuickTime menus work as before.
                const userLang = selectedAudioLangRef.current;
                const defaultLang = audioOrderRef.current?.[0] ?? "";
                // TEMP-SW-TEST: forzar ruta nativa para que el SW intervenga.
                // Sin esto, el caso interesante (user eligió idioma ≠ DEFAULT) toma
                // la ruta MSE y el SW nunca corre. Revertir tras validar SW en STG.
                const useMsePath = !!userLang && !!defaultLang && userLang !== defaultLang;
                usedMsePathRef.current = useMsePath;
                dbgFs("onEnterFullscreen: path decision", {
                    userLang,
                    defaultLang,
                    useMsePath,
                });

                if (useMsePath) {
                    // Plan B: keep MSE pipeline alive. Don't touch reactHls, don't
                    // destroy our own hls instance, don't reassign video.src. iOS will
                    // open QuickTime over the existing element and continue playing.
                    // savedReactHlsRef stays null — the exit handler uses it as the
                    // signal for whether a reattach is needed.
                    dbgFs("onEnterFullscreen: MSE path — keeping hls.js attached");
                } else {
                    if (reactHls) {
                        savedReactHlsRef.current = reactHls;
                        reactHls.stopLoad();
                        reactHls.detachMedia();
                        dbgFs("onEnterFullscreen: hls.js detached");
                    }

                    // Silence the change event fired by the track teardown that iOS triggers
                    // synchronously when video.src is reassigned. Without this, the listener
                    // would interpret the teardown (mode: showing → disabled) as a user CC-off
                    // action before any applyNativeSubtitles call has set lastApplyAt.
                    lastApplyAt = Date.now();
                    destroyHls();

                    // Append the user's audio language as a query param ONLY when the SW
                    // API is available (i.e., HTTPS or localhost). When present and active,
                    // the SW intercepts the .m3u8 fetch, strips the param, fetches the
                    // original manifest and returns it rewritten with the user's language
                    // as DEFAULT. On HTTP origins (e.g., LAN dev access from a phone),
                    // SW is disabled by the browser, so appending the param would only
                    // bust the CDN cache without any benefit — skip it.
                    let srcToUse = playerUrl;
                    const swSupported = "serviceWorker" in navigator;
                    const swController = (navigator as any).serviceWorker?.controller;
                    const swControlling = !!swController;

                    // Same-origin precheck: the SW only intercepts requests on the
                    // app's origin. If the m3u8 lives on a CDN/different host the SW
                    // never sees the fetch, regardless of being registered. This log
                    // is the diagnostic that tells us whether the whole approach is
                    // even viable for this manifest.
                    let manifestOrigin: string | null = null;
                    let sameOrigin = false;
                    try {
                        manifestOrigin = new URL(playerUrl ?? "", window.location.href).origin;
                        sameOrigin = manifestOrigin === window.location.origin;
                    } catch (e) {
                        dbgFs("onEnterFullscreen: same-origin parse failed", String(e));
                    }
                    dbgFs("onEnterFullscreen: SW preflight", {
                        swSupported,
                        swControlling,
                        controllerUrl: swController?.scriptURL ?? null,
                        appOrigin: window.location.origin,
                        manifestOrigin,
                        sameOrigin,
                        hasUserLang: !!userLang,
                        userLang,
                    });

                    if (userLang && swSupported && swControlling && sameOrigin) {
                        try {
                            const u = new URL(playerUrl, window.location.href);
                            u.searchParams.set("_epicAudio", userLang);
                            srcToUse = u.toString();
                            dbgFs("onEnterFullscreen: appended _epicAudio param", {
                                target: userLang,
                                swControlling,
                                sameOrigin,
                            });
                        } catch (e) {
                            dbgFs("onEnterFullscreen: URL construction failed, using original:", e);
                        }
                    } else {
                        dbgFs("onEnterFullscreen: skipping _epicAudio param", {
                            hasTarget: !!userLang,
                            swSupported,
                            swControlling,
                            sameOrigin,
                            reason: !sameOrigin ? "cross-origin manifest — SW cannot intercept" :
                                    !swControlling ? "SW not yet controlling page" :
                                    !swSupported ? "SW not supported" : "no userLang",
                        });
                    }

                    video.src = srcToUse;
                    video.load();
                    dbgFs("onEnterFullscreen: native src set, load() called", {
                        srcSuffix: srcToUse.length > 80 ? "..." + srcToUse.slice(-80) : srcToUse,
                        hasEpicAudioParam: srcToUse.includes("_epicAudio="),
                    });
                }

                // Listen for tracks added after loadedmetadata (QuickTime adds tracks dynamically).
                // Retry after 150ms — WebKit may reset mode immediately after addtrack fires.
                const onTrackAdded = (event: any) => {
                    const newTrack = event?.track;
                    dbgFs("onEnterFullscreen: addtrack event fired", {
                        newTrack: newTrack ? {
                            kind: newTrack.kind,
                            label: newTrack.label,
                            language: newTrack.language,
                            initialMode: newTrack.mode,
                        } : "no track on event",
                        totalCount: video.textTracks.length,
                        reactPlayerEnabled: subtitlesEnabledRef.current,
                    });

                    applyNativeSubtitles("addtrack-immediate");
                    setTimeout(() => applyNativeSubtitles("addtrack-150ms"), 150);
                };

                // Detects subtitle mode changes that DIDN'T come from our own
                // applyNativeSubtitles. These are either iOS auto-adjustments or,
                // more importantly, the user toggling CC in QuickTime's menu —
                // catching those live lets react-player stay in sync without
                // waiting for fullscreen exit (and survives if the user closes
                // the modal without exiting fullscreen first).
                const onTextTracksChange = () => {
                    const isNativeFs = Boolean(
                        (video as any).webkitDisplayingFullscreen ||
                        (document as any).webkitIsFullScreen
                    );
                    if (!isNativeFs) {
                        if (!subtitlesEnabledRef.current) {
                            applyNativeSubtitles("change-inline-disabled-enforce");
                        }
                        return;
                    }
                    if (Date.now() - lastApplyAt < APPLY_QUIET_WINDOW_MS) return;
                    const subTracks = Array.from(
                        { length: video.textTracks.length },
                        (_, i) => video.textTracks[i],
                    ).filter(t => t.kind === "subtitles");
                    if (subTracks.length === 0) return;
                    const userWantsOn = subTracks.some(t => t.mode === "showing");
                    if (userWantsOn !== subtitlesEnabledRef.current) {
                        dbgFs("textTracks change: detected user CC menu action", {
                            from: subtitlesEnabledRef.current,
                            to: userWantsOn,
                            modes: subTracks.map(t => t.mode),
                        });
                        syncSubtitlesEnabled(userWantsOn);
                        if (userWantsOn) {
                            applyNativeSubtitles("change-user-on");
                        }
                    }
                };

                (video.textTracks as any).addEventListener("addtrack", onTrackAdded);
                (video.textTracks as any).addEventListener("change", onTextTracksChange);
                addTrackCleanupRef.current = () => {
                    (video.textTracks as any).removeEventListener("addtrack", onTrackAdded);
                    (video.textTracks as any).removeEventListener("change", onTextTracksChange);
                };

                // Fallback: audio tracks may arrive after loadedmetadata via addtrack
                // on video.audioTracks (iOS adds alternate renditions asynchronously).
                let audioTrackApplied = false;
                const onAudioTrackAdded = () => {
                    if (!audioTrackApplied) {
                        applyNativeAudio("audioTracks-addtrack");
                        audioTrackApplied = true;
                    }
                };
                (video as any).audioTracks?.addEventListener("addtrack", onAudioTrackAdded);
                addTrackCleanupRef.current = (() => {
                    const prev = addTrackCleanupRef.current;
                    return () => {
                        prev?.();
                        (video as any).audioTracks?.removeEventListener("addtrack", onAudioTrackAdded);
                    };
                })();

                // Under the MSE path we did NOT reassign video.src, so loadedmetadata
                // won't fire again — the existing pipeline keeps playing seamlessly,
                // hls.js already has the correct audio rendition active, and the
                // text tracks it manages stay in place. Subtitle native sync is still
                // handled live by the addtrack/change listeners above.
                if (!useMsePath) {
                    if (!isLiveType) {
                        video.addEventListener("loadedmetadata", () => {
                            dbgFs("onEnterFullscreen loadedmetadata: restoring time", savedTime);
                            logVideoTextTracks(video, "onEnterFullscreen loadedmetadata");
                            video.currentTime = savedTime;
                            applyNativeSubtitles("loadedmetadata");
                            applyNativeAudio("loadedmetadata");
                            if ((video as any).audioTracks?.length > 1) audioTrackApplied = true;
                            video.play().catch(() => {void 0;});
                        }, {once: true});
                    } else {
                        video.addEventListener("loadedmetadata", () => {
                            applyNativeSubtitles("loadedmetadata-live");
                            applyNativeAudio("loadedmetadata-live");
                            if ((video as any).audioTracks?.length > 1) audioTrackApplied = true;
                        }, {once: true});
                        video.play().catch(() => {void 0;});
                    }
                }
            };

            const onExitFullscreen = () => {
                // Stop tracking newly added native tracks — hls.js takes over from here
                addTrackCleanupRef.current?.();
                addTrackCleanupRef.current = null;

                // timeupdate gives the last reliable position; video.currentTime at
                // webkitendfullscreen is stale because iOS hasn't synced the native
                // player position back to the element at this exact moment.
                video.removeEventListener("timeupdate", onTimeUpdate);
                const currentTime = trackedTime > 0 ? trackedTime : video.currentTime;

                const wasMsePath = usedMsePathRef.current;
                usedMsePathRef.current = false;

                // MSE path: hls.js was never detached, so there's nothing to reattach
                // and no reinit needed — playback was continuous from the SourceBuffer.
                // Still sync subtitle state in case the user toggled CC inside QuickTime.
                if (wasMsePath) {
                    dbgFs("onExitFullscreen: MSE path — no reattach, no handleHls", {
                        currentTime,
                        trackedTime,
                    });
                    const finalSubTracks = Array.from(
                        {length: video.textTracks.length},
                        (_, i) => video.textTracks[i],
                    ).filter(t => t.kind === "subtitles");
                    if (finalSubTracks.length > 0) {
                        const userWantsOn = finalSubTracks.some(t => t.mode === "showing");
                        if (userWantsOn !== subtitlesEnabledRef.current) {
                            dbgFs("onExitFullscreen [MSE]: syncing CC state", {
                                from: subtitlesEnabledRef.current,
                                to: userWantsOn,
                            });
                            syncSubtitlesEnabled(userWantsOn);
                        }
                    }
                    return;
                }

                // Signal useVideoPlayer to skip the API-position resume seek — it would
                // override the exit position. Cleared by VideoPlayer.tsx after seeking.
                if (!isLiveType && currentTime > 0) {
                    iosExitSeekRef.current = currentTime;
                }

                const reactHls = savedReactHlsRef.current;
                savedReactHlsRef.current = null;

                dbgFs("onExitFullscreen", { currentTime, trackedTime, isLiveType, hasReactHls: !!reactHls });
                logVideoTextTracks(video, "onExitFullscreen — BEFORE reattach");

                // Capture the user's final CC decision from inside QuickTime by
                // reading the textTrack modes AFTER the session. addtrack.initialMode
                // is unreliable (iOS reports "showing" from AUTOSELECT even when its
                // CC menu reflects a persisted OFF), but the final state at exit is
                // the authoritative record of what the user did in the CC menu.
                // Belt-and-braces with the live "change" listener: this catches the
                // case where the listener's quiet window swallowed the user action.
                const finalSubTracks = Array.from(
                    { length: video.textTracks.length },
                    (_, i) => video.textTracks[i],
                ).filter(t => t.kind === "subtitles");
                if (finalSubTracks.length > 0) {
                    const userWantsOn = finalSubTracks.some(t => t.mode === "showing");
                    if (userWantsOn !== subtitlesEnabledRef.current) {
                        dbgFs("onExitFullscreen: syncing react-player to QuickTime final state", {
                            from: subtitlesEnabledRef.current,
                            to: userWantsOn,
                            modes: finalSubTracks.map(t => t.mode),
                        });
                        syncSubtitlesEnabled(userWantsOn);
                    }
                }

                // Capture the audio track the user selected in QuickTime. In native-HLS
                // mode (set on enter via video.src = playerUrl), video.audioTracks has all
                // audio renditions from the manifest with correct enabled state. Read them
                // before reattaching hls.js, which overwrites the native audio pipeline.
                const nativeAudioTracks = (video as any).audioTracks;
                if (nativeAudioTracks?.length > 1) {
                    for (let i = 0; i < nativeAudioTracks.length; i++) {
                        if (nativeAudioTracks[i].enabled) {
                            const label = nativeAudioTracks[i].label || nativeAudioTracks[i].language;
                            if (label) {
                                dbgFs("onExitFullscreen: QuickTime audio track:", label, "idx:", i);
                                syncAudioLang(label);
                            }
                            break;
                        }
                    }
                }

                if (reactHls) {
                    reactHls.attachMedia(video);
                    reactHls.loadSource(playerUrl);
                    dbgFs("onExitFullscreen: hls.js reattached + loadSource called");
                    if (!isLiveType && currentTime > 0) {
                        video.addEventListener("loadedmetadata", () => {
                            dbgFs("onExitFullscreen loadedmetadata: restoring time", currentTime);
                            logVideoTextTracks(video, "onExitFullscreen loadedmetadata");
                            video.currentTime = currentTime;
                            video.play().catch(() => {void 0;});
                        }, {once: true});
                    }
                }

                dbgFs("onExitFullscreen: calling handleHls");
                handleHls(playerUrl, {isLiveContent: isLiveType});
            };

            video.addEventListener("webkitbeginfullscreen", onEnterFullscreen);
            video.addEventListener("webkitendfullscreen", onExitFullscreen);

            cleanupRef.current = () => {
                video.removeEventListener("webkitbeginfullscreen", onEnterFullscreen);
                video.removeEventListener("webkitendfullscreen", onExitFullscreen);
                video.removeEventListener("timeupdate", onTimeUpdate);
            };
        },
        [playerRef, playerUrl, isLiveType, destroyHls, handleHls, syncSubtitlesEnabled, syncAudioLang]
    );

    return {attach};
};
